import classNames from 'classnames';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { ChangeEvent, useCallback, useEffect } from 'react';
import { RRule, Weekday } from 'rrule';
import {
  Days,
  Frequency,
  datesOptions,
  daysOptions,
  frequencyOptions,
} from '@/modules/admin-dashboard/defaults/reminders.ts';
import { Select, TextInput } from 'ui';
import { TrashIcon } from 'ui/icons';
import { CreateDueRemainderRequest } from 'compliance-api';

interface ReminderItemProps {
  disabled?: boolean;
  index: number;
  errors?: Record<keyof CreateDueRemainderRequest, string>;
  onChange: (index: number, value: Partial<CreateDueRemainderRequest>) => void;
  onDelete: (index: number) => void;
  remainder: CreateDueRemainderRequest;
}

const ReminderItem = ({
  index,
  disabled,
  remainder,
  onChange,
  onDelete,
  errors,
}: ReminderItemProps) => {
  const { frequency, days_before_due, is_when_overdue, stop_after } = remainder;

  const currentRRule = RRule.fromString(frequency);
  const {
    origOptions: { freq, interval, bysetpos, byweekday, bymonthday },
  } = currentRRule;

  const adaptedFrequency =
    freq !== undefined && interval !== undefined ? `${interval}_${freq}` : Frequency.NEVER;

  const adaptedDay = `${bysetpos}_${get(byweekday, '[0].weekday')}`;

  const adaptedFrequencyOptions = remainder.is_when_overdue
    ? frequencyOptions
    : frequencyOptions.filter(({ value }) => value !== Frequency.NEVER);

  useEffect(() => {
    const isNever = adaptedFrequency === Frequency.NEVER;
    if (is_when_overdue && !isNever && stop_after === null) {
      onChange(index, { stop_after: 90 });
    }
  }, [is_when_overdue, adaptedFrequency, stop_after, index, onChange]);

  const handleChangeDaysBefore = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = +e.target.value;
      const isWeeklyFrequency = adaptedFrequency === Frequency.EVERY_WEEK;

      // Validate max values: 12 weeks or 90 days
      if (isWeeklyFrequency && inputValue > 12) return;
      if (!isWeeklyFrequency && inputValue > 90) return;

      // Convert weeks to days if frequency is "Every week"
      const daysBefore = isWeeklyFrequency ? inputValue * 7 : inputValue;

      if (daysBefore < 0) return;
      onChange(index, { days_before_due: daysBefore, stop_after: undefined });
    },
    [index, onChange, adaptedFrequency],
  );

  const handleChangeFrequency = useCallback(
    (option: Frequency | null) => {
      if (!option) return;
      const isNever = option === Frequency.NEVER;
      const isEveryDay = option === Frequency.EVERY_DAY;
      const isEveryWeek = option === Frequency.EVERY_WEEK;

      const [interval, freq] = option.split('_');

      let newRuleOptions;

      if (isNever) {
        newRuleOptions = omit(currentRRule.origOptions, [
          'freq',
          'interval',
          'byhour',
          'byminute',
          'bysetpos',
          'byweekday',
        ]);
      } else if (isEveryDay) {
        newRuleOptions = omit(
          {
            ...currentRRule.origOptions,
            freq: +freq,
            interval: +interval,
          },
          ['bysetpos', 'byweekday'],
        );
      } else if (isEveryWeek) {
        newRuleOptions = omit(
          {
            ...currentRRule.origOptions,
            freq: +freq,
            interval: +interval,
            bysetpos: 1,
            byweekday: RRule.MO,
          },
          ['byhour', 'byminute'],
        );
      } else {
        newRuleOptions = omit(
          {
            ...currentRRule.origOptions,
            freq: +freq,
            interval: +interval,
            bymonthday: 1,
          },
          ['byhour', 'byminute', 'bysetpos', 'byweekday'],
        );
      }

      const newRule = new RRule(newRuleOptions);
      const value: Partial<CreateDueRemainderRequest> = { frequency: newRule.toString() };

      if (!is_when_overdue) {
        value.stop_after = undefined;
      }

      onChange(index, value);
    },
    [currentRRule.origOptions, index, onChange, is_when_overdue],
  );

  const handleChangeDate = useCallback(
    (option: number | null) => {
      if (!option) return;

      const newRule = new RRule({
        ...currentRRule.origOptions,
        bymonthday: option,
      });

      const value: Partial<CreateDueRemainderRequest> = { frequency: newRule.toString() };

      if (!is_when_overdue) {
        value.stop_after = undefined;
      }

      onChange(index, value);
    },
    [currentRRule.origOptions, index, onChange, is_when_overdue],
  );

  const handleChangeDay = useCallback(
    (option: Days | null) => {
      if (!option) return;
      const [bysetpos, byweekday] = option.split('_');

      const newRule = new RRule({
        ...currentRRule.origOptions,
        byweekday: new Weekday(+byweekday),
        bysetpos: +bysetpos,
      });

      const value: Partial<CreateDueRemainderRequest> = { frequency: newRule.toString() };

      if (!is_when_overdue) {
        value.stop_after = undefined;
      }

      onChange(index, value);
    },
    [currentRRule.origOptions, index, onChange, is_when_overdue],
  );

  // const handleChangeTime = useCallback(
  //   (e: ChangeEvent<HTMLInputElement>) => {
  //     const [hours, minutes] = e.target.value.split(':');
  //
  //     const newRule = new RRule({
  //       ...currentRRule.origOptions,
  //       byhour: +hours,
  //       byminute: +minutes,
  //     });
  //
  //     onChange(index, { frequency: newRule.toString() });
  //   },
  //   [currentRRule.origOptions, index, onChange],
  // );

  const handleChangeStopAfter = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const stopAfter = +e.target.value;

      if (stopAfter < 1 || stopAfter > 90) return;
      onChange(index, { stop_after: stopAfter });
    },
    [index, onChange],
  );

  return (
    <div className="flex flex-col gap-4">
      <div
        className={classNames('grid gap-4', {
          'grid-cols-[4fr_5fr_4fr_auto]': ![Frequency.EVERY_DAY, Frequency.NEVER].includes(
            adaptedFrequency as Frequency,
          ),
          'grid-cols-[4fr_9fr_auto]': [Frequency.EVERY_DAY, Frequency.NEVER].includes(
            adaptedFrequency as Frequency,
          ),
        })}
      >
        {is_when_overdue ? (
          <div className="self-center">If task is Overdue</div>
        ) : (
          <TextInput
            disabled={disabled}
            onChange={handleChangeDaysBefore}
            name="days"
            label={
              adaptedFrequency === Frequency.EVERY_WEEK
                ? 'Weeks Before Due date'
                : 'Days Before Due date'
            }
            type="number"
            value={
              adaptedFrequency === Frequency.EVERY_WEEK
                ? Math.floor((days_before_due || 0) / 7)
                : days_before_due || ''
            }
            className="min-w-0 w-full"
            error={errors?.days_before_due}
            min={0}
            max={adaptedFrequency === Frequency.EVERY_WEEK ? 12 : 90}
          />
        )}
        <Select
          disabled={disabled}
          label="FREQUENCY"
          positionMenu="up"
          options={adaptedFrequencyOptions}
          value={adaptedFrequency as Frequency}
          onChange={handleChangeFrequency}
          error={errors?.frequency}
        />
        {![Frequency.EVERY_DAY, Frequency.EVERY_WEEK, Frequency.NEVER].includes(
          adaptedFrequency as Frequency,
        ) && (
          <Select
            label="On day"
            positionMenu="up"
            options={datesOptions}
            value={bymonthday as number | null}
            onChange={handleChangeDate}
            isClearable={true}
            isMulti={false}
            disabled={adaptedFrequency === Frequency.NEVER || disabled}
          />
        )}
        {adaptedFrequency === Frequency.EVERY_WEEK && (
          <Select
            disabled={disabled}
            label="DAY"
            positionMenu="up"
            options={daysOptions}
            value={adaptedDay as Days}
            onChange={handleChangeDay}
          />
        )}
        {/*{adaptedFrequency === Frequency.EVERY_DAY && (*/}
        {/*  <TextInput*/}
        {/*    disabled={disabled}*/}
        {/*    name={`time_reminder_${index}`}*/}
        {/*    type="time"*/}
        {/*    className="pt-7 min-w-0 w-full"*/}
        {/*    value={`${byhour?.toString().padStart(2, '0')}:${byminute?.toString().padStart(2, '0')}`}*/}
        {/*    onChange={handleChangeTime}*/}
        {/*  />*/}
        {/*)}*/}

        <button
          type="button"
          disabled={disabled}
          onClick={onDelete.bind(this, index)}
          className={classNames({ 'opacity-0': is_when_overdue })}
        >
          <img src={TrashIcon} alt="remove" />
        </button>
      </div>
      {is_when_overdue && adaptedFrequency !== Frequency.NEVER && (
        <div className="flex items-end gap-2">
          <TextInput
            disabled={disabled}
            onChange={handleChangeStopAfter}
            name="stop_after"
            label="Stop after"
            type="number"
            value={stop_after ?? 90}
            className="min-w-0 w-32"
            error={errors?.stop_after}
            min={1}
            max={90}
          />
          <span className="pb-3 text-sm text-gray-900">Days</span>
        </div>
      )}
    </div>
  );
};

export default ReminderItem;
